/*
 * Cart free gift: keeps the cart's gift lines and gift discount code in step
 * with what the cart has earned. The cart-wide version of free-gift-cart.js
 * (the product page's Free Gift Tiers): same rules for codes, customer codes,
 * busy state and flicker, but gifts unlock on spend instead of packs.
 *
 * Rules come from snippets/cart-free-gift.liquid (#CartGiftRules), set under
 * Theme settings > Cart free gift. The progress bar itself is Liquid, re-
 * rendered with the drawer; this file only animates its fill.
 *
 * WHAT COUNTS AS "EARNED"
 * ---------------------------------------------------------------------------
 * Spend = every line's final price, except gift lines and Shipping
 * Protection (the same rule as the bar). A gift unlocks at its threshold.
 *
 * GIFT LINES
 * ---------------------------------------------------------------------------
 * Each gift line carries the hidden property `_free_gift: "cart-<n>"` (n =
 * the gift's slot in the settings) and is kept at quantity 1. Lines for a
 * slot that's no longer set up are removed.
 *
 * MAKING GIFTS FREE (DISCOUNT CODES)
 * ---------------------------------------------------------------------------
 * Each gift has a code (Buy X Get Y, code-only) that brings it to its set
 * price (0 = free). Only the highest earned gift's code is applied — one
 * purchase can't qualify two Buy X Get Y codes — so that code must cover
 * every gift up to its threshold. A gift with no code is never added.
 *
 * A gift line is never left above its set price: if it is after the codes
 * are applied, it's removed, and not re-added until the cart changes
 * (sessionStorage, keyed on the non-gift lines).
 *
 * CUSTOMER CODES
 * ---------------------------------------------------------------------------
 * A customer's own code is tried alongside the gift code. If Shopify applies
 * both, both stay. If not, the gifts win and the customer's code is dropped
 * quietly. Only if the gifts still can't be made free does their code come
 * back. Codes typed at checkout can't be seen from the theme.
 *
 * WHEN THIS RUNS
 * ---------------------------------------------------------------------------
 * On page load and on every cart change: PUB_SUB_EVENTS.cartUpdate (product
 * forms, cart.js), and the cart:refresh / cart:updated events (drawer
 * upsell). After writing it publishes its own cartUpdate (source
 * 'cart-free-gift', ignored here) so the drawer and cart page repaint, and
 * refreshes the header cart count.
 */
(function () {
  if (window.__cartFreeGiftLoaded) return;
  window.__cartFreeGiftLoaded = true;

  var GIFT_PROPERTY_KEY = '_free_gift';
  var TIER_PREFIX = 'cart-';
  var CHOICE_KEY = 'cartGiftChoice:v1';
  var SUPPRESS_KEY = 'cartGiftSuppressed:v1';

  function readJSON(storage, key) {
    try {
      return JSON.parse(storage.getItem(key));
    } catch (e) {
      return null;
    }
  }

  function writeJSON(storage, key, value) {
    try {
      storage.setItem(key, JSON.stringify(value));
    } catch (e) {}
  }

  var rulesEl = document.getElementById('CartGiftRules');
  if (!rulesEl) return;
  var rules;
  try {
    rules = JSON.parse(rulesEl.textContent);
  } catch (e) {
    return;
  }
  var tierConfig = rules.tiers || {};
  var excluded = (rules.excludeProductIds || []).map(String);
  if (!Object.keys(tierConfig).length) return;

  var giftCodes = {};
  Object.keys(tierConfig).forEach(function (tier) {
    var code = (tierConfig[tier].code || '').trim();
    if (code) giftCodes[code.toUpperCase()] = true;
  });
  var usesCodes = Object.keys(giftCodes).length > 0;
  // Lets the drawer hide a gift line that isn't at its price yet (it's about
  // to be removed), only when codes are configured.
  if (usesCodes) document.documentElement.classList.add('free-gift-codes');

  // Gift codes from the product-page gift themes, still in some carts: ours,
  // so they get replaced, not kept as the customer's.
  var RETIRED_CODES = /^(GIFTTEST|PK\d+|ASG\d)-/i;

  function isGiftCode(code) {
    var c = String(code).toUpperCase();
    return !!giftCodes[c] || RETIRED_CODES.test(c);
  }

  function normCodes(codes) {
    return codes
      .map(function (c) {
        return String(c).toUpperCase();
      })
      .sort()
      .join(',');
  }

  function cartCodes(cart) {
    return (cart.discount_codes || []).map(function (d) {
      return d.code;
    });
  }

  function customerCodesIn(cart) {
    return cartCodes(cart).filter(function (c) {
      return !isGiftCode(c);
    });
  }

  function loadChoice(token) {
    var choice = readJSON(localStorage, CHOICE_KEY);
    return choice && choice.token === token ? choice : null;
  }

  function saveChoice(token, choice, codes, sig) {
    writeJSON(localStorage, CHOICE_KEY, { token: token, choice: choice, codes: codes, sig: sig || null });
  }

  function clearChoice() {
    try {
      localStorage.removeItem(CHOICE_KEY);
    } catch (e) {}
  }

  function isGiftLine(item) {
    return !!(item.properties && item.properties[GIFT_PROPERTY_KEY]);
  }

  // A gift line this file manages (product-page gifts use other tier names).
  function isCartGiftLine(item) {
    return isGiftLine(item) && String(item.properties[GIFT_PROPERTY_KEY]).indexOf(TIER_PREFIX) === 0;
  }

  function cartSignature(cart) {
    return (
      cart.items
        .filter(function (item) {
          return !isGiftLine(item);
        })
        .map(function (item) {
          return item.variant_id + ':' + item.quantity;
        })
        .sort()
        .join(',') +
      '|' +
      cart.currency
    );
  }

  function loadSuppressed(sig) {
    var s = readJSON(sessionStorage, SUPPRESS_KEY);
    return s && s.sig === sig ? s.tiers : [];
  }

  // Same rule as the bar (snippets/cart-free-gift.liquid).
  function spendOf(cart) {
    return cart.items.reduce(function (sum, item) {
      if (isGiftLine(item) || excluded.indexOf(String(item.product_id)) !== -1) return sum;
      return sum + item.final_line_price;
    }, 0);
  }

  function earnedTiers(cart) {
    var spend = spendOf(cart);
    return Object.keys(tierConfig).filter(function (tier) {
      return spend >= tierConfig[tier].threshold;
    });
  }

  // tier -> the one line kept for it; extra lines for a tier, and lines for a
  // slot no longer set up, are `stale`.
  function giftLines(cart) {
    var byTier = {};
    var stale = [];
    cart.items.forEach(function (item) {
      if (!isCartGiftLine(item)) return;
      var tier = item.properties[GIFT_PROPERTY_KEY];
      if (!tierConfig[tier] || byTier[tier]) {
        stale.push(item.key);
        return;
      }
      byTier[tier] = {
        key: item.key,
        quantity: item.quantity,
        atPrice: item.final_line_price <= (tierConfig[tier].price || 0) * item.quantity,
      };
    });
    return { byTier: byTier, stale: stale };
  }

  // The code of the highest tier in `tiers` that has one.
  function codeForTiers(tiers) {
    var best = null;
    tiers.forEach(function (tier) {
      var config = tierConfig[tier];
      if (!config || !(config.code || '').trim()) return;
      if (!best || config.threshold > tierConfig[best].threshold) best = tier;
    });
    return best ? tierConfig[best].code.trim() : null;
  }

  var wrote = false;

  // While a sync runs, block clicks on the cart items (CSS in cart-drawer.liquid)
  // so a shopper's remove/quantity change can't be sent at the same time as a
  // gift update: two concurrent writes to one cart can lose one of them.
  function setCartBusy(busy) {
    document.documentElement.classList.toggle('free-gift-busy', busy);
  }

  function postJSON(url, body) {
    wrote = true;
    return fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body),
    }).then(function (r) {
      if (!r.ok) throw new Error('cart-free-gift: ' + url + ' returned ' + r.status);
      return r.json();
    });
  }

  // Brings the cart to exactly `targetTiers` gift lines and (when
  // `touchCodes`) exactly `targetCodes`. Resolves to the resulting cart.
  function applyTarget(cart, targetTiers, targetCodes, touchCodes) {
    var lines = giftLines(cart);
    var updates = {};
    Object.keys(lines.byTier).forEach(function (tier) {
      var line = lines.byTier[tier];
      if (targetTiers.indexOf(tier) === -1) updates[line.key] = 0;
      else if (line.quantity !== 1) updates[line.key] = 1;
    });
    lines.stale.forEach(function (key) {
      updates[key] = 0;
    });

    // Only gifts that have a code are ever added: the code is what brings
    // them to their price.
    var toAdd = targetTiers.filter(function (tier) {
      var config = tierConfig[tier];
      return !lines.byTier[tier] && config.available !== false && (config.code || '').trim();
    });
    var codesChange = touchCodes && normCodes(cartCodes(cart)) !== normCodes(targetCodes);

    if (!toAdd.length && !Object.keys(updates).length && !codesChange) {
      return Promise.resolve(cart);
    }

    // Removals and the discount code go first, adds last: a gift added to a
    // cart that already carries its code comes back at its price in the add
    // response itself, so nothing ever shows it at full price for a moment.
    var needUpdate = Object.keys(updates).length > 0 || codesChange;
    var body = { updates: updates };
    if (touchCodes) body.discount = targetCodes.join(',');

    return (needUpdate ? postJSON(window.routes.cart_update_url, body) : Promise.resolve(cart)).then(function (updated) {
      if (!toAdd.length) return updated;
      return postJSON(window.routes.cart_add_url, {
        items: toAdd.map(function (tier) {
          var properties = {};
          properties[GIFT_PROPERTY_KEY] = tier;
          return { id: tierConfig[tier].variantId, quantity: 1, properties: properties };
        }),
      }).then(function () {
        return fetch('/cart.js').then(function (r) {
          return r.json();
        });
      });
    });
  }

  // Never leave a gift above its price: remove any gift line that is, and
  // don't re-add that tier until the rest of the cart changes.
  function enforcePrice(cart) {
    var lines = giftLines(cart);
    var over = Object.keys(lines.byTier).filter(function (tier) {
      return !lines.byTier[tier].atPrice;
    });
    if (!over.length) return Promise.resolve(cart);

    var sig = cartSignature(cart);
    writeJSON(sessionStorage, SUPPRESS_KEY, { sig: sig, tiers: loadSuppressed(sig).concat(over) });
    var keep = Object.keys(lines.byTier).filter(function (tier) {
      return over.indexOf(tier) === -1;
    });
    var code = codeForTiers(keep);
    return applyTarget(cart, keep, customerCodesIn(cart).concat(code ? [code] : []), true);
  }

  var syncing = false;
  var pendingResync = false;

  function reconcile(cart) {
    var earned = earnedTiers(cart);
    var sig = cartSignature(cart);
    var suppressed = loadSuppressed(sig);
    var giftTiers = earned.filter(function (tier) {
      return suppressed.indexOf(tier) === -1;
    });
    var customerCodes = customerCodesIn(cart);
    var stored = readJSON(localStorage, CHOICE_KEY);
    if (stored && stored.token !== cart.token) clearChoice();
    var choice = loadChoice(cart.token);

    // No codes set up yet: gifts show on the bar only. Just clear out lines.
    if (!usesCodes) return applyTarget(cart, [], [], false);

    if (!earned.length) {
      if (choice) clearChoice();
      return applyTarget(cart, [], customerCodes, true);
    }

    var giftCode = codeForTiers(giftTiers);
    var ours = giftCode ? [giftCode] : [];

    if (!customerCodes.length) {
      var choseGifts = choice && choice.choice === 'gifts' && choice.codes && choice.codes.length;
      if (choice && !choseGifts) clearChoice();
      return applyTarget(cart, giftTiers, ours, true)
        .then(enforcePrice)
        .then(function (result) {
          if (!choseGifts) return result;
          if (Object.keys(giftLines(result).byTier).length) return result;
          // Their code was dropped for gifts Shopify won't make free: put the
          // code back rather than leave them with neither.
          saveChoice(cart.token, 'kept', choice.codes, sig);
          return applyTarget(result, [], choice.codes, true);
        });
    }

    // Gifts couldn't be made free with this code dropped (see above): keep it.
    if (choice && choice.choice === 'kept' && choice.sig === sig && normCodes(choice.codes) === normCodes(customerCodes)) {
      return applyTarget(cart, [], customerCodes, true);
    }

    // Unknown whether this code stacks with the gifts: apply both and let
    // Shopify decide.
    return applyTarget(cart, giftTiers, customerCodes.concat(ours), true).then(function (result) {
      var oursApply = ours.every(function (code) {
        return (result.discount_codes || []).some(function (d) {
          return d.code.toUpperCase() === code.toUpperCase() && d.applicable;
        });
      });
      // When they can't combine, Shopify keeps whichever saves more and
      // marks the other not applicable.
      var customerApplies = (result.discount_codes || []).every(function (d) {
        return isGiftCode(d.code) || d.applicable;
      });
      if (ours.length && oursApply && customerApplies) {
        clearChoice();
        return enforcePrice(result);
      }
      // They can't combine: the gifts win, and the customer's code goes.
      forgetLinkCode();
      saveChoice(cart.token, 'gifts', customerCodes);
      return applyTarget(result, giftTiers, ours, true)
        .then(enforcePrice)
        .then(function (after) {
          if (Object.keys(giftLines(after).byTier).length) return after;
          saveChoice(cart.token, 'kept', customerCodes, sig);
          return applyTarget(after, [], customerCodes, true);
        });
    });
  }

  // A /discount/CODE link sets this cookie, which Shopify can re-apply (e.g.
  // at checkout) and swap the gift code back out.
  function forgetLinkCode() {
    var expire = 'discount_code=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT';
    document.cookie = expire;
    document.cookie = expire + '; domain=' + location.hostname;
    document.cookie = expire + '; domain=.' + location.hostname.replace(/^www\./, '');
  }

  // The header's cart count: gifts change it, and the drawer's own repaint
  // after a cartUpdate doesn't include it.
  function refreshCartCount() {
    var bubble = document.getElementById('cart-icon-bubble');
    if (!bubble) return;
    fetch(window.Shopify && Shopify.routes ? Shopify.routes.root + '?sections=cart-icon-bubble' : '/?sections=cart-icon-bubble')
      .then(function (r) {
        return r.json();
      })
      .then(function (sections) {
        var html = new DOMParser().parseFromString(sections['cart-icon-bubble'], 'text/html');
        var fresh = html.querySelector('.shopify-section');
        if (fresh) bubble.innerHTML = fresh.innerHTML;
      })
      .catch(function () {});
  }

  function syncGifts() {
    if (syncing) {
      pendingResync = true;
      return;
    }
    syncing = true;
    wrote = false;
    setCartBusy(true);

    fetch('/cart.js')
      .then(function (r) {
        return r.json();
      })
      .then(reconcile)
      .catch(function (e) {
        console.error(e);
      })
      .finally(function () {
        // Repaint even after a partial failure so the drawer shows what changed.
        // After writing, stay "busy" until the drawer has re-rendered with the
        // new cart: until then the old markup may still hold a mid-update gift,
        // which must keep its free look rather than blink.
        if (wrote) {
          awaitDrawerRender();
          if (typeof publish === 'function') publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-free-gift' });
          refreshCartCount();
        } else if (!eventTimer && !awaitingItemsEl) {
          setCartBusy(false);
        }
        syncing = false;
        if (pendingResync) {
          pendingResync = false;
          syncGifts();
        } else if (wrote && verifyPasses < 2) {
          // Other cart scripts (e.g. shipping-protection.js) also write on
          // cart changes; a write of theirs racing ours can undo it. Re-check
          // once things settle (a sync with nothing to change writes nothing).
          verifyPasses++;
          setTimeout(syncGifts, 1200);
        }
      });
  }

  var verifyPasses = 0;
  var eventTimer = null;

  // The drawer re-renders by replacing its <cart-drawer-items> element, so a
  // new element means the fresh markup is in. Release then, or after 3s.
  var awaitingItemsEl = null;
  var releaseTimer = null;

  function awaitDrawerRender() {
    var itemsEl = document.querySelector('cart-drawer-items');
    if (!itemsEl) {
      setCartBusy(false);
      return;
    }
    awaitingItemsEl = itemsEl;
    clearTimeout(releaseTimer);
    releaseTimer = setTimeout(releaseAfterRender, 3000);
  }

  function releaseAfterRender() {
    awaitingItemsEl = null;
    clearTimeout(releaseTimer);
    if (!syncing && !eventTimer) setCartBusy(false);
  }

  // Collapse bursts of cart events, and let other scripts reacting to the
  // same event finish their write before this one starts.
  function syncSoon() {
    verifyPasses = 0;
    setCartBusy(true);
    clearTimeout(eventTimer);
    eventTimer = setTimeout(function () {
      eventTimer = null;
      syncGifts();
    }, 300);
  }

  // ---- Progress bar: animate the fill from where it was ----

  var lastFill = null;
  function animateFill() {
    var fill = document.querySelector('[data-cart-gift-fill]');
    if (!fill || fill.dataset.animated) return;
    fill.dataset.animated = '1';
    var target = fill.style.width;
    if (lastFill !== null && lastFill !== target) {
      fill.style.transition = 'none';
      fill.style.width = lastFill;
      void fill.offsetWidth;
      fill.style.transition = '';
      fill.style.width = target;
    }
    lastFill = target;
  }

  // ---- Popup: a gift circle opens its image and name, larger ----

  var modal = document.getElementById('CartGiftModal');
  if (modal) {
    document.body.appendChild(modal);
    modal.addEventListener('click', function () {
      modal.hidden = true;
      document.body.style.overflow = '';
    });
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && !modal.hidden) modal.click();
    });
  }
  document.addEventListener('click', function (e) {
    var marker = e.target.closest && e.target.closest('[data-cart-gift-marker]');
    if (!marker || !modal) return;
    e.preventDefault();
    var img = modal.querySelector('[data-cart-gift-modal-image]');
    var title = modal.querySelector('[data-cart-gift-modal-title]');
    if (img) {
      img.hidden = !marker.dataset.image;
      if (marker.dataset.image) img.src = marker.dataset.image;
    }
    if (title) title.textContent = marker.dataset.title || '';
    modal.hidden = false;
    document.body.style.overflow = 'hidden';
  });

  // The drawer's contents are replaced on every cart update.
  var drawerEl = document.querySelector('cart-drawer');
  if (drawerEl && window.MutationObserver) {
    new MutationObserver(function () {
      if (awaitingItemsEl && document.querySelector('cart-drawer-items') !== awaitingItemsEl) releaseAfterRender();
      animateFill();
    }).observe(drawerEl, { childList: true, subtree: true });
  }
  animateFill();

  if (typeof subscribe === 'function') {
    subscribe(PUB_SUB_EVENTS.cartUpdate, function (event) {
      if (event && event.source === 'cart-free-gift') return;
      syncSoon();
    });
  }
  // The drawer upsell adds with its own fetch and announces it this way.
  document.addEventListener('cart:refresh', syncSoon);
  document.addEventListener('cart:updated', syncSoon);

  // Page load: only when the cart has something in it.
  if (!document.querySelector("cart-drawer.is-empty")) syncGifts();
})();
